import { useRef } from "react";
import { useQuery, useQueryClient } from "react-query";

import { TilesStatus, getShapefilesTilesStatus } from "../../../../api/shapefile";
import { PROJECT_SHAPEFILES_QUERY_KEYS } from "./useProjectShapefilesQuery";

export const SHAPEFILES_TILES_STATUS_QUERY_KEY = "shapefilesTilesStatus";

interface UseShapefilesTilesStatusQueryParams {
  shapefileIds: string[];
  enabled?: boolean;
  projectId?: string;
}

export function useShapefilesTilesStatusQuery({
  shapefileIds,
  enabled = true,
  projectId
}: UseShapefilesTilesStatusQueryParams) {
  const queryClient = useQueryClient();
  const pollCountRef = useRef(0);

  return useQuery(
    [SHAPEFILES_TILES_STATUS_QUERY_KEY, shapefileIds],
    () => getShapefilesTilesStatus(shapefileIds),
    {
      enabled: enabled && shapefileIds.length > 0,
      refetchInterval: (data) => {
        if (!data || !Array.isArray(data)) return false;

        const hasProcessing = data.some(
          (item) =>
            item.tilesStatus === TilesStatus.Pending ||
            item.tilesStatus === TilesStatus.Processing ||
            item.tilesStatus === TilesStatus.ProcessingHighZoom
        );

        if (!hasProcessing) {
          pollCountRef.current = 0;
          return false;
        }
        // exponential backoff for polling maxed at 10 seconds
        const delay = Math.min(Math.pow(2, pollCountRef.current) * 1000, 10000);
        pollCountRef.current += 1;

        return delay;
      },
      refetchIntervalInBackground: true,
      refetchOnWindowFocus: false,
      retry: false,
      useErrorBoundary: false,
      onSuccess: (data) => {
        if (data && Array.isArray(data) && projectId) {
          const hasCompleted = data.some(
            (item) =>
              item.tilesReady === true || item.tilesStatus === TilesStatus.Completed
          );

          if (hasCompleted) {
            queryClient.invalidateQueries([
              PROJECT_SHAPEFILES_QUERY_KEYS.currentProjectShapefiles,
              projectId
            ]);
          }
        }
      }
    }
  );
}
